import { useState, useEffect } from "react";

export interface NetworkStatus {
  /** Whether the browser currently has a network connection. */
  isOnline: boolean;
  /**
   * Becomes `true` after the first transition from offline → online,
   * so the overlay can show the "back online" success state.
   */
  wasOffline: boolean;
}

/**
 * Actively probes the network with a no-cors fetch.
 * Returns `true` if online, `false` otherwise.
 *
 * Exported standalone so it can be called outside of the hook —
 * pair it with `useConnectionGuard` when you need the overlay to react.
 */
export async function checkConnection(): Promise<boolean> {
  try {
    await fetch("https://www.google.com/favicon.ico", {
      mode: "no-cors",
      cache: "no-store",
    });
    if (!navigator.onLine) window.dispatchEvent(new Event("online"));
    return true;
  } catch {
    return false;
  }
}

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState<boolean>(() => navigator.onLine);
  const [wasOffline, setWasOffline] = useState<boolean>(false);

  useEffect(() => {
    const handleOnline = (): void => {
      setIsOnline(true);
      setWasOffline(true);
    };

    const handleOffline = (): void => {
      setIsOnline(false);
      setWasOffline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return { isOnline, wasOffline };
}
